// tabs/FacilitiesTab.js - Updated with React-PDF integration
import React, { useState, useEffect } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { pdf } from '@react-pdf/renderer';
import { makeRequest2 } from "../../../../utils/makeRequest";
import { toastify } from "../../../../utils/toast";
import { getFacilityInvoices } from "../utils/invoiceFetcher";
import InvoicePDF from "../pdf/InvoicePDF";

function FacilitiesTab({
  facilities,
  facilityUnits,
  facilityInvoices,
  loading,
  onViewInvoice,
  searchTerm,
  onSearch,
  onRefreshFacilityInvoices,
}) {
  const [expandedRows, setExpandedRows] = useState([]);
  const [loadingInvoices, setLoadingInvoices] = useState({});
  const [facilityInvoicesData, setFacilityInvoicesData] = useState({});

  const handleGenerateForFacility = async (facilityId) => {
    try {
      const response = await makeRequest2(
        `/api/core/billing/facility/${facilityId}/generate-invoice`,
        "POST",
        {},
      );
      if (response.success) {
        toastify(`Invoices generated for facility!`, "success");
        // Refresh invoices for this facility after generation
        await loadFacilityInvoices(facilityId);
      } else {
        toastify("Failed to generate: " + response.error, "error");
      }
    } catch (error) {
      toastify("Error: " + error.message, "error");
    }
  };

  const loadFacilityInvoices = async (facilityId) => {
    setLoadingInvoices((prev) => ({ ...prev, [facilityId]: true }));

    try {
      console.log(`Loading invoices for facility: ${facilityId}`);
      const result = await getFacilityInvoices(facilityId, {
        showToast: false, // Don't show toast for individual loads
      });

      console.log("getFacilityInvoices result:", result);

      if (result && result.success) {
        // Ensure invoices is always an array
        const invoices = Array.isArray(result.invoices) ? result.invoices : [];

        setFacilityInvoicesData((prev) => ({
          ...prev,
          [facilityId]: invoices,
        }));

        // Also update parent component's state if callback is provided
        if (onRefreshFacilityInvoices) {
          onRefreshFacilityInvoices(facilityId, invoices);
        }

        console.log(
          `Successfully loaded ${invoices.length} invoices for facility ${facilityId}`,
        );
      } else {
        const errorMsg = result?.error || "Unknown error occurred";
        console.error(
          `Failed to load invoices for facility ${facilityId}:`,
          errorMsg,
        );
        toastify(`Failed to load invoices: ${errorMsg}`, "error");

        // Set empty array on failure
        setFacilityInvoicesData((prev) => ({
          ...prev,
          [facilityId]: [],
        }));
      }
    } catch (error) {
      console.error(
        `Error loading invoices for facility ${facilityId}:`,
        error,
      );
      toastify(`Error loading invoices: ${error.message}`, "error");

      // Set empty array on error
      setFacilityInvoicesData((prev) => ({
        ...prev,
        [facilityId]: [],
      }));
    } finally {
      setLoadingInvoices((prev) => ({ ...prev, [facilityId]: false }));
    }
  };

  const handleRowToggle = async (e) => {
    const newExpandedRows = e.data;
    setExpandedRows(newExpandedRows);

    // Find newly expanded rows
    const previouslyExpandedIds = expandedRows.map((row) => row._id);
    const currentlyExpandedIds = newExpandedRows.map((row) => row._id);

    const newlyExpanded = currentlyExpandedIds.filter(
      (id) => !previouslyExpandedIds.includes(id),
    );

    // Load invoices for newly expanded facilities
    for (const facilityId of newlyExpanded) {
      if (!facilityInvoicesData[facilityId]) {
        await loadFacilityInvoices(facilityId);
      }
    }
  };

  const actionsTemplate = (rowData) => {
    return (
      <Button
        icon="ti ti-plus"
        className="btn btn-primary btn-sm"
        onClick={() => handleGenerateForFacility(rowData._id)}
        tooltip="Generate Invoice"
      />
    );
  };

  const unitsDataTemplate = (rowData, field) => {
    const units = facilityUnits[rowData._id] || {};
    return units[field] || 0;
  };

  const rowExpansionTemplate = (data) => {
    const facilitySpecificInvoices = facilityInvoicesData[data._id] || [];
    const isLoadingForFacility = loadingInvoices[data._id] || false;
    const facility = facilities.find(f => f._id === data._id);

    return (
      <FacilityInvoicesExpansion
        data={data}
        facility={facility}
        facilityInvoices={facilitySpecificInvoices}
        onViewInvoice={onViewInvoice}
        loading={isLoadingForFacility}
        onRefreshInvoices={() => loadFacilityInvoices(data._id)}
      />
    );
  };

  return (
    <DataTable
      value={facilities}
      loading={loading}
      paginator
      rows={10}
      rowsPerPageOptions={[5, 10, 25, 50]}
      emptyMessage="No facilities found"
      globalFilter={searchTerm}
      onFilter={(e) => onSearch(e.filters.global.value)}
      expandedRows={expandedRows}
      onRowToggle={handleRowToggle}
      rowExpansionTemplate={rowExpansionTemplate}
    >
      <Column expander style={{ width: "3em" }} />
      <Column field="name" header="Facility Name" sortable />
      <Column
        header="Total Active Units"
        body={(rowData) => unitsDataTemplate(rowData, "totalActiveUnits")}
        sortable
      />
      <Column
        header="Units with Contract"
        body={(rowData) => unitsDataTemplate(rowData, "unitsWithContract")}
        sortable
      />
      <Column
        header="Units with Lease"
        body={(rowData) => unitsDataTemplate(rowData, "unitsWithLease")}
        sortable
      />
      <Column header="Actions" body={actionsTemplate} />
    </DataTable>
  );
}

const FacilityInvoicesExpansion = ({
  data,
  facility,
  facilityInvoices,
  onViewInvoice,
  loading,
  onRefreshInvoices,
}) => {
  const [activeStatusTab, setActiveStatusTab] = useState("all");
  const [filteredInvoices, setFilteredInvoices] = useState([]);

  useEffect(() => {
    let invoices = facilityInvoices;
    if (activeStatusTab !== "all") {
      invoices = invoices.filter(
        (invoice) =>
          invoice.status.toLowerCase() === activeStatusTab.toLowerCase(),
      );
    }
    setFilteredInvoices(invoices);
  }, [activeStatusTab, facilityInvoices]);

  // Helper functions for PDF generation
  const getDisplayHeaderSettings = (invoice) => {
    return {
      companyName: invoice?.header?.companyName || "",
      companyAddress: invoice?.header?.companyAddress || "",
      companyPhone: invoice?.header?.companyPhone || "",
      companyEmail: invoice?.header?.companyEmail || "",
      companyWebsite: invoice?.header?.companyWebsite || "",
      taxNumber: invoice?.header?.taxNumber || "",
      logoPreview: invoice?.header?.logo || null,
    };
  };

  const getDisplayFooterSettings = (invoice) => {
    return {
      bankName: invoice?.footer?.bankName || "",
      accountName: invoice?.footer?.accountName || "",
      accountNumber: invoice?.footer?.accountNumber || "",
      swiftCode: invoice?.footer?.swiftCode || "",
      paymentTerms: invoice?.footer?.paymentTerms || "Net 30 days",
      latePaymentFee: invoice?.footer?.latePaymentFee || "",
      taxRate: invoice?.footer?.taxRate || "",
      additionalNotes: invoice?.footer?.additionalNotes || "",
      supportEmail: invoice?.footer?.supportEmail || "",
      supportPhone: invoice?.footer?.supportPhone || "",
      termsAndConditions: invoice?.footer?.termsAndConditions || "",
    };
  };

  const getPDFFilename = (invoice) => {
    const invoiceNumber = invoice?.invoiceNumber || "INV-0000";
    const facilityName = invoice?.facilityName || facility?.name || "Invoice";
    const date = new Date().toISOString().split("T")[0];
    return `${invoiceNumber}_${facilityName.replace(/[^a-zA-Z0-9]/g, "_")}_${date}.pdf`;
  };

  const handlePrintInvoice = async (invoice) => {
    try {
      toastify("Preparing invoice for printing...", "info");

      const displayHeaderSettings = getDisplayHeaderSettings(invoice);
      const displayFooterSettings = getDisplayFooterSettings(invoice);

      const pdfBlob = await pdf(
        <InvoicePDF
          invoice={invoice}
          facility={facility}
          displayHeaderSettings={displayHeaderSettings}
          displayFooterSettings={displayFooterSettings}
        />
      ).toBlob();

      const url = URL.createObjectURL(pdfBlob);
      const printWindow = window.open(url, '_blank');

      if (printWindow) {
        printWindow.onload = () => {
          setTimeout(() => {
            printWindow.print();
          }, 500);
        };

        setTimeout(() => {
          URL.revokeObjectURL(url);
        }, 5000);
      } else {
        toastify("Popup blocked. Please allow popups to print.", "warn");
      }

      toastify("Invoice prepared for printing!", "success");
    } catch (error) {
      console.error("Print error:", error);
      toastify(`Error printing invoice: ${error.message}`, "error");
    }
  };

  const handleDownloadInvoice = async (invoice) => {
    try {
      toastify("Generating PDF...", "info");

      const displayHeaderSettings = getDisplayHeaderSettings(invoice);
      const displayFooterSettings = getDisplayFooterSettings(invoice);

      const pdfBlob = await pdf(
        <InvoicePDF
          invoice={invoice}
          facility={facility}
          displayHeaderSettings={displayHeaderSettings}
          displayFooterSettings={displayFooterSettings}
        />
      ).toBlob();

      const url = URL.createObjectURL(pdfBlob);
      const a = document.createElement("a");
      a.href = url;
      a.download = getPDFFilename(invoice);
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toastify("PDF downloaded successfully!", "success");
    } catch (error) {
      console.error("Download error:", error);
      toastify(`Error downloading PDF: ${error.message}`, "error");
    }
  };

  const getStatusTabCounts = () => {
    const counts = {
      all: facilityInvoices.length,
      pending: facilityInvoices.filter(
        (inv) => inv.status.toLowerCase() === "pending",
      ).length,
      paid: facilityInvoices.filter(
        (inv) => inv.status.toLowerCase() === "paid",
      ).length,
      overdue: facilityInvoices.filter(
        (inv) => inv.status.toLowerCase() === "overdue",
      ).length,
    };
    return counts;
  };

  const counts = getStatusTabCounts();

  const statusTemplate = (rowData) => {
    const getStatusClass = (status) => {
      switch (status?.toLowerCase()) {
        case "paid":
          return "text-bg-success";
        case "pending":
          return "text-bg-warning";
        case "overdue":
          return "text-bg-danger";
        default:
          return "text-bg-secondary";
      }
    };
    return (
      <span className={`badge rounded-pill ${getStatusClass(rowData.status)}`}>
        {rowData.status}
      </span>
    );
  };

  const amountTemplate = (rowData) => {
    return <strong>KES {rowData.amount?.toLocaleString() || "0"}</strong>;
  };

  const actionTemplate = (rowData) => {
    return (
      <div className="btn-group" role="group">
        <Button
          icon="pi pi-eye"
          className="btn btn-outline-info btn-sm"
          onClick={() => onViewInvoice(rowData)}
          tooltip="View Details"
          tooltipOptions={{ position: "top" }}
        />
        <Button
          icon="pi pi-print"
          className="btn btn-outline-success btn-sm"
          onClick={() => handlePrintInvoice(rowData)}
          tooltip="Print"
          tooltipOptions={{ position: "top" }}
        />
        <Button
          icon="pi pi-download"
          className="btn btn-outline-primary btn-sm"
          onClick={() => handleDownloadInvoice(rowData)}
          tooltip="Download"
          tooltipOptions={{ position: "top" }}
        />
      </div>
    );
  };

  const invoiceNumberTemplate = (rowData) => {
    return (
      <div>
        <strong>{rowData.invoiceNumber}</strong>
        <br />
        <small className="text-muted">
          {rowData.invoiceType?.toUpperCase()} Invoice
        </small>
      </div>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  if (loading) {
    return (
      <div className="text-center p-4">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-2">Loading invoices for {data.name}...</p>
      </div>
    );
  }

  return (
    <div className="p-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="mb-0">Invoices for {data.name}</h5>
        <Button
          icon="pi pi-refresh"
          className="btn btn-outline-secondary btn-sm"
          onClick={onRefreshInvoices}
          tooltip="Refresh Invoices"
        />
      </div>

      {/* Status Tabs */}
      <div className="mb-3">
        <ul className="nav nav-pills nav-justified" role="tablist">
          <li className="nav-item">
            <button
              className={`nav-link ${activeStatusTab === "all" ? "active" : ""}`}
              onClick={() => setActiveStatusTab("all")}
              type="button"
            >
              <i className="ti ti-list me-1"></i>
              All
              <span className="badge bg-primary ms-2">{counts.all}</span>
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${activeStatusTab === "pending" ? "active" : ""}`}
              onClick={() => setActiveStatusTab("pending")}
              type="button"
            >
              <i className="ti ti-clock me-1"></i>
              Pending
              <span className="badge bg-warning ms-2">{counts.pending}</span>
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${activeStatusTab === "paid" ? "active" : ""}`}
              onClick={() => setActiveStatusTab("paid")}
              type="button"
            >
              <i className="ti ti-check me-1"></i>
              Paid
              <span className="badge bg-success ms-2">{counts.paid}</span>
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${activeStatusTab === "overdue" ? "active" : ""}`}
              onClick={() => setActiveStatusTab("overdue")}
              type="button"
            >
              <i className="ti ti-alert-triangle me-1"></i>
              Overdue
              <span className="badge bg-danger ms-2">{counts.overdue}</span>
            </button>
          </li>
        </ul>
      </div>

      {/* Summary Cards */}
      <div className="row mb-4">
        <div className="col-md-3">
          <div className="card bg-primary text-white">
            <div className="card-body">
              <div className="d-flex align-items-center">
                <div className="flex-grow-1">
                  <h6 className="text-white mb-0">Total</h6>
                  <h4 className="text-white mb-0">{counts.all}</h4>
                </div>
                <div className="flex-shrink-0">
                  <i className="ti ti-receipt fs-2 opacity-75"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card bg-warning text-white">
            <div className="card-body">
              <div className="d-flex align-items-center">
                <div className="flex-grow-1">
                  <h6 className="text-white mb-0">Pending</h6>
                  <h4 className="text-white mb-0">{counts.pending}</h4>
                </div>
                <div className="flex-shrink-0">
                  <i className="ti ti-clock fs-2 opacity-75"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card bg-success text-white">
            <div className="card-body">
              <div className="d-flex align-items-center">
                <div className="flex-grow-1">
                  <h6 className="text-white mb-0">Paid</h6>
                  <h4 className="text-white mb-0">{counts.paid}</h4>
                </div>
                <div className="flex-shrink-0">
                  <i className="ti ti-check fs-2 opacity-75"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card bg-danger text-white">
            <div className="card-body">
              <div className="d-flex align-items-center">
                <div className="flex-grow-1">
                  <h6 className="text-white mb-0">Overdue</h6>
                  <h4 className="text-white mb-0">{counts.overdue}</h4>
                </div>
                <div className="flex-shrink-0">
                  <i className="ti ti-alert-triangle fs-2 opacity-75"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Invoices Data Table */}
      <DataTable
        value={filteredInvoices}
        emptyMessage="No invoices found"
        sortMode="multiple"
        paginator
        rows={10}
        stripedRows
        tableStyle={{ minWidth: "50rem" }}
        className="p-datatable-sm"
      >
        <Column
          field="invoiceNumber"
          sortable
          header="Invoice #"
          body={invoiceNumberTemplate}
          style={{ width: "150px" }}
        />
        <Column
          field="amount"
          sortable
          header="Amount"
          body={amountTemplate}
          style={{ width: "120px" }}
        />
        <Column
          field="dueDate"
          sortable
          header="Due Date"
          body={(rowData) => formatDate(rowData.dueDate)}
          style={{ width: "120px" }}
        />
        <Column
          field="status"
          header="Status"
          body={statusTemplate}
          style={{ width: "100px" }}
        />
        <Column
          field="createdAt"
          sortable
          header="Created"
          body={(rowData) => formatDate(rowData.createdAt)}
          style={{ width: "120px" }}
        />
        <Column
          header="Actions"
          body={actionTemplate}
          style={{ width: "180px" }}
        />
      </DataTable>
    </div>
  );
};

export default FacilitiesTab;